import { type TextareaHTMLAttributes, type FC } from 'react'
import cn from '@/utils/classNames'
import styles from '../Input/Input.module.scss'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Optional label for the textarea
   */
  label?: string

  /**
   * If true, applies error styling
   */
  hasError?: boolean

  /**
   * Error message if validation fails
   */
  errorMessage?: string

  /**
   * Input style variant
   * - 'default': white background
   * - 'transparent': linear with white border
   */
  variant?: 'default' | 'transparent'
}

/**
 * A reusable textarea component with built-in label and styling.
 *
 * @component
 * @example
 * <Textarea
 *   id="message"
 *   name="message"
 *   required
 *   placeholder="Write your message..."
 * />
 */
const Textarea: FC<TextareaProps> = ({ label, className, hasError, errorMessage, ...rest }) => {
  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={rest.id} className={styles.label}>
          {label}
        </label>
      )}
      <textarea
        className={cn(
          styles.input,
          hasError && styles.error,
          rest.variant === 'transparent' && styles.transparent,
          className
        )}
        {...rest}
      />
      {hasError && errorMessage && (
        <p className={styles.errorMessage}>{errorMessage}</p>
      )}
    </div>
  )
}

export default Textarea
